import React from "react";

// MATERIAL UI COMPONENTS
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import Grid from "@mui/material/Grid";
import Divider from "@mui/material/Divider";
import Chip from "@mui/material/Chip";
import Button from "@mui/material/Button";
import { alpha, useTheme } from "@mui/material";

// ICONS
import PaidIcon from "@mui/icons-material/Paid";
import AccessTimeFilledIcon from "@mui/icons-material/AccessTimeFilled";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

// COLORS
import { blue, green, grey, red, orange } from "@mui/material/colors";
import { elapsedTimeMask } from "../../../common/dateMask";
import { formatFinance } from "../../../utils/formatters";

export default function EnhancedSaloonTable({
  table,
  newOrder,
  openOrderDetails,
  action,
}) {
  const theme = useTheme();

  // Generate random number of seats if not provided (between 2 and 8)
  const seats =
    table.seats || (table.index % 3 === 0 ? 2 : table.index % 5 === 0 ? 6 : 4);

  // For the table activity status
  const getElapsedTime = () => {
    if (!table.order?.created_at) return null;

    const createdDate = new Date(table.order.created_at);
    const now = new Date();
    return Math.floor((now - createdDate) / (1000 * 60));
  };

  const elapsedMinutes = getElapsedTime();
  const isOccupied = !!table.order?.order_id;

  const handleCloseTableClick = (e) => {
    e.stopPropagation();
    action(table);
  };

  const handleTableBlockClick = (e) => {
    e.preventDefault();
    if (isOccupied) {
      openOrderDetails(table);
    } else {
      newOrder(table);
    }
  };

  return (
    <Paper
      elevation={0}
      onClick={handleTableBlockClick}
      sx={{
        borderRadius: 4,
        overflow: "hidden",
        height: "100%",
        cursor: "pointer",
        position: "relative",
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        border: "1px solid",
        borderColor: isOccupied
          ? alpha(theme.palette.primary.main, 0.2)
          : alpha(theme.palette.divider, 0.1),
        bgcolor: isOccupied
          ? alpha(theme.palette.primary.main, 0.02)
          : "background.paper",
        boxShadow: "0 2px 12px 0 rgba(0,0,0,0.03)",
        "&:hover": {
          transform: "translateY(-4px)",
          boxShadow: isOccupied
            ? `0 12px 24px -10px ${alpha(theme.palette.primary.main, 0.2)}`
            : "0 12px 24px -10px rgba(0,0,0,0.1)",
          borderColor: isOccupied
            ? theme.palette.primary.main
            : theme.palette.text.secondary,
        },
      }}
    >
      {/* Top Status Bar */}
      <Box
        sx={{
          height: 6,
          width: "100%",
          bgcolor: isOccupied
            ? elapsedMinutes > 60
              ? red[400]
              : elapsedMinutes > 30
              ? orange[400]
              : green[400]
            : grey[200],
        }}
      />

      <Box sx={{ p: 2.5 }}>
        {/* Header */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ mb: 2 }}
        >
          <Box>
            <Typography variant="h5" fontWeight="800" lineHeight={1}>
              {table.index + 1}
            </Typography>
            <Typography variant="caption" color="text.secondary" fontWeight="600">
              MESA
            </Typography>
          </Box>

          <Chip
            icon={<PeopleAltIcon sx={{ fontSize: "16px !important" }} />}
            label={`${seats}`}
            size="small"
            sx={{
              fontWeight: "bold",
              bgcolor: isOccupied ? "background.paper" : grey[100],
              border: "1px solid",
              borderColor: "divider",
            }}
          />
        </Stack>

        {isOccupied ? (
          /* Occupied State */
          <>
            <Box sx={{ mb: 2 }}>
              <Typography
                variant="subtitle1"
                fontWeight="700"
                noWrap
                sx={{ mb: 0.5 }}
              >
                {table.order?.customer_name || "Cliente sem nome"}
              </Typography>

              <Stack direction="row" spacing={2} alignItems="center">
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <AccessTimeFilledIcon
                    sx={{
                      fontSize: 16,
                      mr: 0.5,
                      color:
                        elapsedMinutes > 60
                          ? red[500]
                          : elapsedMinutes > 30
                          ? orange[500]
                          : green[500],
                    }}
                  />
                  <Typography
                    variant="body2"
                    fontWeight="600"
                    color="text.secondary"
                  >
                    {elapsedTimeMask(table.order?.created_at)}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <PaidIcon
                    sx={{ fontSize: 16, mr: 0.5, color: "text.secondary" }}
                  />
                  <Typography variant="body2" fontWeight="700">
                    {formatFinance(table.order?.total ?? 0)}
                  </Typography>
                </Box>
              </Stack>
            </Box>

            <Divider sx={{ mb: 2 }} />

            <Stack direction="row" spacing={1}>
              <Button
                size="small"
                variant="contained"
                disableElevation
                fullWidth
                onClick={handleCloseTableClick}
                color="success"
                startIcon={<CheckCircleIcon />}
                sx={{
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: "bold",
                  bgcolor: alpha(green[500], 0.1),
                  color: green[700],
                  "&:hover": {
                    bgcolor: alpha(green[500], 0.2),
                  },
                }}
              >
                Fechar
              </Button>
              <Button
                size="small"
                variant="outlined"
                fullWidth
                endIcon={<ArrowForwardIcon />}
                sx={{
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: "bold",
                  borderColor: "divider",
                  color: "text.primary",
                }}
              >
                Detalhes
              </Button>
            </Stack>
          </>
        ) : (
          /* Free State */
          <Box
            sx={{
              height: 100,
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              border: "2px dashed",
              borderColor: "divider",
              borderRadius: 2,
              color: "text.secondary",
            }}
          >
            <AddCircleOutlineIcon sx={{ fontSize: 32, mb: 1, opacity: 0.5 }} />
            <Typography variant="body2" fontWeight="600">
              Livre
            </Typography>
          </Box>
        )}
      </Box>
    </Paper>
  );
}
